import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import type { ProspectListOrigin } from "../../api/client.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";

/** The short word for where a collection came from; a missing origin is a hand-made one. */
export function originKindLabel(origin: ProspectListOrigin | null): string {
  switch (origin?.kind) {
    case "order":
      return "Order";
    case "workflow":
      return "Workflow";
    case "agent":
      return "Agent";
    case "mcp":
      return "MCP";
    default:
      return "Manual";
  }
}

/**
 * The link to what made a collection: the order (an organization page) or the
 * workflow (a tenant page); nothing for the other origins.
 */
function originTo(origin: ProspectListOrigin, tenantPath: (subpath?: string) => string): string | null {
  if (!origin.id) return null;
  if (origin.kind === "order") return `/organization/orders/${origin.id}`;
  if (origin.kind === "workflow") return tenantPath(`/workflows/${origin.id}`);
  return null;
}

/**
 * Where a collection came from, as "Kind" plus a link to the order or workflow
 * when there is one. Used in the Collections table and a collection's header.
 */
export default function CollectionOrigin({
  origin,
  variant = "cell",
}: {
  origin: ProspectListOrigin | null;
  /** `cell`: label over a caption link (table). `inline`: "Kind: name" in running text. */
  variant?: "cell" | "inline";
}) {
  const { tenantPath } = useTenant();
  const label = originKindLabel(origin);
  const to = origin ? originTo(origin, tenantPath) : null;
  const name = origin?.name ?? null;
  // Clicks on the link must not also open the row it sits in.
  const nameNode = to ? (
    <Link component={RouterLink} to={to} underline="hover" onClick={(e) => e.stopPropagation()}>
      {name ?? label}
    </Link>
  ) : (
    name
  );

  if (variant === "inline") {
    if (origin?.kind === "mcp") return <>via MCP</>;
    if (origin?.kind === "agent") return <>by an agent</>;
    if (!nameNode) return <>{label.toLowerCase()}</>;
    return (
      <>
        {label}: {nameNode}
      </>
    );
  }
  return (
    <>
      <Typography variant="body2">{label}</Typography>
      {nameNode && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 260 }}>
          {nameNode}
        </Typography>
      )}
    </>
  );
}
